
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref,
  PropType
} from "vue";
import { useRouter } from "vue-router";
import { useToast, useConfirm } from "../../modules/message";
import {
  useOrcamentoService,
  RenovacaoLicencaPreviewPayload
} from "../../modules/business/orcamento/orcamentoBusiness";
import { useGabaritoService } from "../../modules/business/licenca/gabaritoBusiness";
import { useTransportadorService } from "../../modules/business/transportador/transportadorBusiness";
import { useEngenheiroService } from "../../modules/business/engenheiro/engenheiroBusiness";
import CadastroLicenca from "../licenca/CadastroLicenca.vue";

interface PreviewState {
  previews: Array<RenovacaoLicencaPreviewPayload>;
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  editingPreview: RenovacaoLicencaPreviewPayload | null;
  editingIndex: number;
}

export default defineComponent({
  components: { CadastroLicenca },
  setup(props, { emit }) {
    const router = useRouter();
    const gabaritoService = useGabaritoService();
    const transportadorService = useTransportadorService();
    const engenheiroService = useEngenheiroService();

    const state = reactive<PreviewState>({
      previews: [],
      stateReady: false,
      loadings: [],
      editingPreview: null,
      editingIndex: -1
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const resultsPreviews = computed(() => ({
      content: state.previews,
      page: {
        size: state.previews.length,
        number: 0,
        totalElements: state.previews.length,
        totalPages: 1
      }
    }));

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const getPlacaTrator = (licenca: any): string => {
      const conj = licenca?.conjuntoTransportador;
      if (Array.isArray(conj)) {
        const trator = conj.find((v: any) => v.veiculoTrator);
        const placa = trator?.veiculoTratorDaLicenca?.placa;
        if (placa) return placa;
      }
      return licenca?.veiculoTratorPrincipal?.placa ?? "—";
    };

    const getCarretas = (licenca: any): string => {
      const conj = licenca?.conjuntoTransportador;
      if (Array.isArray(conj)) {
        const placas = conj
          .filter((v: any) => v.carreta && v.carretaDaLicenca?.placa)
          .map((v: any) => v.carretaDaLicenca.placa);
        if (placas.length) return placas.join(" + ");
      }
      return "—";
    };

    const getNomeGabarito = (licenca: any): string => {
      return gabaritoService.getNomeGabarito(licenca?.idGabarito) ?? "—";
    };

    const getNomeTransportador = (licenca: any): string => {
      return (
        transportadorService.getNomeTransportador(licenca?.transportador) ?? "—"
      );
    };

    const getNomeEngenheiro = (licenca: any): string => {
      return engenheiroService.getNomeEngenheiro(licenca?.engenheiro) ?? "—";
    };

    const getRotuloOrigem = (
      preview: RenovacaoLicencaPreviewPayload
    ): string => {
      const num = preview?.numeroAetOrigem;
      if (num && num.trim().length > 0) return num;
      return "#" + preview.idLicencaMae;
    };

    const getDimensoesResumo = (licenca: any): string => {
      const a = licenca?.alturaTotal;
      const l = licenca?.larguraTotal;
      const c = licenca?.comprimentoTotal;
      const fmt = (n: any) => (n != null && !isNaN(+n) ? (+n).toFixed(2) : "—");
      return `A ${fmt(a)} × L ${fmt(l)} × C ${fmt(c)}`;
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const carregarPreviews = () => {
      const { getPreviewLicencas, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      getPreviewLicencas(props.idOrcamento)
        .then(() => {
          state.previews =
            (data.value as Array<RenovacaoLicencaPreviewPayload>) ?? [];
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.stateReady = true;
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const abrirEditar = (
      preview: RenovacaoLicencaPreviewPayload,
      index: number
    ) => {
      state.editingPreview = JSON.parse(JSON.stringify(preview));
      state.editingIndex = index;
    };

    const cancelarEdicao = () => {
      state.editingPreview = null;
      state.editingIndex = -1;
    };

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const onSavePreview = (entity: any) => {
      if (state.editingPreview) {
        // Linhas da mesma idLicencaMae compartilham o mesmo clone (1 copia
        // por mae sera gerada). Propaga a edicao para todas as linhas
        // dessa mae para a UI ficar consistente.
        const idMae = state.editingPreview.idLicencaMae;
        state.previews = state.previews.map((p) =>
          p.idLicencaMae === idMae
            ? { ...p, licenca: JSON.parse(JSON.stringify(entity)) }
            : p
        );
      }
      cancelarEdicao();
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const abrirLicencaOrigem = (idLicenca: number) => {
      const url = router.resolve({
        name: "licenca-edit",
        params: { idLicenca: idLicenca + "" }
      }).fullPath;
      window.open(url, "_blank");
    };

    const confirmarAceitar = () => {
      useConfirm().require({
        message:
          "Ao aceitar, as licenças-cópia serão criadas e enviadas aos robôs " +
          "dos órgãos. Isso não pode ser desfeito. Continuar?",
        header: "Confirmar geração de AETs",
        icon: "pi pi-info-circle",
        accept: () => {
          const {
            aceitarEtapaRenovacao,
            loading,
            data
          } = useOrcamentoService();
          state.loadings.push(loading);
          aceitarEtapaRenovacao(props.idOrcamento, state.previews)
            .then(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              useToast().success("AETs solicitadas aos robôs");
              emit("after-aceitar", data.value);
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        }
      });
    };

    onMounted(() => carregarPreviews());

    return {
      resultsPreviews,
      getPlacaTrator,
      getCarretas,
      getNomeGabarito,
      getNomeTransportador,
      getNomeEngenheiro,
      getRotuloOrigem,
      getDimensoesResumo,
      abrirEditar,
      cancelarEdicao,
      onSavePreview,
      abrirLicencaOrigem,
      confirmarAceitar,
      stateLoading,
      ...toRefs(state)
    };
  },
  props: {
    idOrcamento: {
      type: Number,
      required: true
    },
    parentLoading: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  }
});
